import React, { useEffect, useState } from "react"
import { Col, Row } from "reactstrap"
import toast from "react-hot-toast"
import { useFormik } from "formik"

import { TextInput } from "components/TextInput"
import { Button } from "components/Button"
import ProductionLineSkeleton from "../ProductionLineSkeleton/ProductionLineSkeleton"
import DailyConfigurationSkeleton from "../ProductionLineSkeleton/DailyConfigurationSkeleton"
import {
   GetProductionLineDetails,
   UpdateProductionLinesDailyConfigurations,
} from "api/api.service"
import {
   initialValues,
   validationSchema,
   FIELDS_NAME,
   MIN_TOTAL_SLOTS,
   MAX_TOTAL_SLOTS,
} from "./FormConfig"
import { DAY_ORDER } from "constants"

const initialDataConfiguration = DAY_ORDER.map(d => ({
   day: d.key,
   premiumSlots: MIN_TOTAL_SLOTS,
   regularSlots: MIN_TOTAL_SLOTS,
}))

const MAX_COLUMN_LIMIT = 6
const NUMBER_REGEX = /^[0-9]*$/

const ProductionLineDetailsDailyConfiguration = props => {
   const {
      productionLineId,
      productionDailyConfigurationDetails,
      isLoading = true,
   } = props
   const [initialData, setInitialData] = useState(initialDataConfiguration)
   const [isUpdating, setIsUpdating] = useState(false)
   const [isDisabled, setIsDisabled] = useState(true)

   const fetchProductionDailyConfig = () => {
      try {
         setIsUpdating(true)
         const formatted = productionDailyConfigurationDetails
            ?.filter(item => !item.validTo)
            ?.map(item => ({
               id: item.id,
               day: item.dailyDay,
               premiumSlots: item.premiumSlots ?? MIN_TOTAL_SLOTS,
               regularSlots: item.regularSlots ?? MIN_TOTAL_SLOTS,
            }))
         const sorted = DAY_ORDER.map(d => {
            const found = formatted?.find(f => f.day === d.key)
            return (
               found || {
                  day: d.key,
                  premiumSlots: MIN_TOTAL_SLOTS,
                  regularSlots: MIN_TOTAL_SLOTS,
                  id: null,
               }
            )
         })
         setInitialData(sorted)
         formik.setValues({
            [FIELDS_NAME.DAILY_CONFIGURATION]: sorted,
         })
      } finally {
         setIsUpdating(false)
      }
   }
   useEffect(() => {
      fetchProductionDailyConfig()
   }, [productionLineId, isLoading, productionDailyConfigurationDetails])
   const handleFetchProductionDailyConfig = async () => {
      try {
         setIsUpdating(true)
         const res = await GetProductionLineDetails(productionLineId)
         return res
      } catch (error) {
         history.push(APP_ROUTES.PRODUCTION_LINE_MANAGEMENT)
         toast.error("Production Line does not exist!")
      } finally {
         setIsUpdating(false)
      }
   }
   const handleSubmit = async values => {
      try {
         setIsUpdating(true)
         const payload = {
            productionLineConfigs: values[FIELDS_NAME.DAILY_CONFIGURATION].map(
               day => ({
                  id: day?.id,
                  premiumSlots: day?.premiumSlots,
                  totalSlots: day?.premiumSlots + day?.regularSlots,
               })
            ),
         }
         await UpdateProductionLinesDailyConfigurations(payload)
         await handleFetchProductionDailyConfig()
         toast.success("Daily Configuration Updated Successfully!")
         setIsDisabled(true)
      } catch (err) {
         console.error(err)
      } finally {
         setIsUpdating(false)
      }
   }
   const formik = useFormik({
      initialValues: initialValues,
      validationSchema: validationSchema,
      onSubmit: handleSubmit,
      validateOnChange: false,
      validateOnBlur: false,
   })
   // useEffect(() => {
   //    formik.setValues({
   //       [FIELDS_NAME.DAILY_CONFIGURATION]: initialData,
   //    })
   // }, [initialData])

   const handleCancel = () => {
      formik.setValues({
         [FIELDS_NAME.DAILY_CONFIGURATION]: initialData,
      })
      setIsDisabled(true)
   }
   return isLoading ? (
      <ProductionLineSkeleton />
   ) : isUpdating ? (
      <DailyConfigurationSkeleton />
   ) : (
      <div className="daily-configuration p-4">
         <h3 className="mb-4">Daily Configuration</h3>
         <form onSubmit={formik.handleSubmit}>
            <Row className="align-items-center mb-3">
               <Col xs="2"></Col>
               {DAY_ORDER.map((d, idx) => (
                  <Col
                     key={d.key}
                     className={
                        "text-center font-weight-bold " +
                        (idx < DAY_ORDER.length - 1 ? "border-right" : "")
                     }
                  >
                     <span className="d-none d-md-inline">{d.full}</span>
                     <span className="d-inline d-md-none">{d.short}</span>
                  </Col>
               ))}
            </Row>
            <Row className="align-items-center mb-3">
               <Col xs="2" className="font-weight-medium">
                  Total Slots
               </Col>
               {DAY_ORDER.map((d, idx) => {
                  const totalValue =
                     formik.values[FIELDS_NAME.DAILY_CONFIGURATION][idx]
                        .premiumSlots +
                     formik.values[FIELDS_NAME.DAILY_CONFIGURATION][idx]
                        .regularSlots
                  return (
                     <Col
                        key={d.key}
                        className={
                           idx < DAY_ORDER.length - 1 ? "border-right" : ""
                        }
                     >
                        <TextInput
                           value={totalValue}
                           readOnly
                           inputClass="bg-light text-center"
                        />
                     </Col>
                  )
               })}
            </Row>
            <Row className="align-items-center mb-3">
               <Col xs="2" className="font-weight-medium">
                  Premium Slots
               </Col>
               {DAY_ORDER.map((d, idx) => {
                  const i = formik.values[
                     FIELDS_NAME.DAILY_CONFIGURATION
                  ].findIndex(c => c.day === d.key)
                  const name = `${FIELDS_NAME.DAILY_CONFIGURATION}[${i}].premiumSlots`
                  const val =
                     formik.values[FIELDS_NAME.DAILY_CONFIGURATION][i]
                        .premiumSlots
                  return (
                     <Col
                        key={d.key}
                        className={idx < MAX_COLUMN_LIMIT ? "border-right" : ""}
                     >
                        <TextInput
                           name={name}
                           value={val}
                           readOnly={isDisabled}
                           onChange={e => {
                              const v = Number(e.target.value)
                              if (!e.target.value.match(NUMBER_REGEX)) {
                                 return
                              }
                              const regularSlots =
                                 formik.values[FIELDS_NAME.DAILY_CONFIGURATION][
                                    i
                                 ].regularSlots
                              formik.setFieldValue(name, v)
                              if (v > MAX_TOTAL_SLOTS) {
                                 formik.setFieldError(
                                    name,
                                    `Max ${MAX_TOTAL_SLOTS}`
                                 )
                              } else {
                                 formik.setFieldError(name, undefined)
                              }
                              if (
                                 regularSlots != MIN_TOTAL_SLOTS &&
                                 v != MIN_TOTAL_SLOTS &&
                                 v + regularSlots > MAX_TOTAL_SLOTS
                              ) {
                                 formik.setFieldError(
                                    `${FIELDS_NAME.DAILY_CONFIGURATION}[${i}]`,
                                    `The sum of Premium & Regular slots should be between ${MIN_TOTAL_SLOTS} and ${MAX_TOTAL_SLOTS}.`
                                 )
                              } else {
                                 formik.setFieldError(
                                    `${FIELDS_NAME.DAILY_CONFIGURATION}[${i}]`,
                                    undefined
                                 )
                              }
                           }}
                           inputClass={`text-center ${
                              formik.errors[FIELDS_NAME.DAILY_CONFIGURATION]?.[
                                 i
                              ]?.premiumSlots
                                 ? "border-danger"
                                 : ""
                           }`}
                        />
                     </Col>
                  )
               })}
            </Row>
            <Row className="align-items-start mb-4">
               <Col xs="2" className="font-weight-medium">
                  Regular Slots
               </Col>
               {DAY_ORDER.map((d, idx) => {
                  const i = formik.values[
                     FIELDS_NAME.DAILY_CONFIGURATION
                  ].findIndex(c => c.day === d.key)
                  const name = `${FIELDS_NAME.DAILY_CONFIGURATION}[${i}].regularSlots`
                  const err =
                     formik.errors[FIELDS_NAME.DAILY_CONFIGURATION]?.[i]
                  return (
                     <Col
                        key={d.key}
                        className={idx < MAX_COLUMN_LIMIT ? "border-right" : ""}
                     >
                        <TextInput
                           name={name}
                           readOnly={isDisabled}
                           value={
                              formik.values[FIELDS_NAME.DAILY_CONFIGURATION][i]
                                 .regularSlots
                           }
                           onChange={e => {
                              const v = Number(e.target.value)
                              if (!e.target.value.match(NUMBER_REGEX)) {
                                 return
                              }
                              const premiumSlots =
                                 formik.values[FIELDS_NAME.DAILY_CONFIGURATION][
                                    i
                                 ].premiumSlots
                              formik.setFieldValue(name, v)
                              if (v > MAX_TOTAL_SLOTS) {
                                 formik.setFieldError(
                                    name,
                                    `Max ${MAX_TOTAL_SLOTS}`
                                 )
                              } else {
                                 formik.setFieldError(name, undefined)
                              }
                              if (
                                 premiumSlots != MIN_TOTAL_SLOTS &&
                                 v != MIN_TOTAL_SLOTS &&
                                 v + premiumSlots > MAX_TOTAL_SLOTS
                              ) {
                                 formik.setFieldError(
                                    `${FIELDS_NAME.DAILY_CONFIGURATION}[${i}]`,
                                    `The sum of Premium & Regular slots should be between ${MIN_TOTAL_SLOTS} and ${MAX_TOTAL_SLOTS}.`
                                 )
                              } else {
                                 formik.setFieldError(
                                    `${FIELDS_NAME.DAILY_CONFIGURATION}[${i}]`,
                                    undefined
                                 )
                              }
                           }}
                           inputClass={`text-center ${
                              err ? "border-danger" : ""
                           }`}
                        />
                        {err && (
                           <div className="text-danger small mt-1">
                              The sum of Premium & Regular slots can should be
                              between {MIN_TOTAL_SLOTS} and {MAX_TOTAL_SLOTS}
                           </div>
                        )}
                     </Col>
                  )
               })}
            </Row>
            {!isDisabled && (
               <div className="d-flex justify-content-end gap-2">
                  <Button
                     className={`header-button headerButtonResponsive note-btn-secondary`}
                     title="Cancel"
                     onClick={handleCancel}
                     type="button"
                  />
                  <Button
                     className={`header-button headerButtonResponsive`}
                     title="Save"
                     type="submit"
                     isLoading={isUpdating}
                  />
               </div>
            )}
         </form>
         {isDisabled && (
            <div className="d-flex justify-content-end">
               <Button
                  className={`header-button headerButtonResponsive`}
                  title="Edit"
                  onClick={() => setIsDisabled(false)}
                  type="button"
               />
            </div>
         )}
      </div>
   )
}
export default ProductionLineDetailsDailyConfiguration
